import { useState } from 'react';
import UploadForm from './components/UploadForm';
import JobStatus from './components/JobStatus';
import { useJobPolling } from './hooks/useJobPolling';
import { createJob, uploadFile, downloadPDF } from './services/api';
import { JobSubmission } from './types';
import { getFileMimeType } from './utils/fileValidation';

function App() {
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { jobStatus, error: pollingError } = useJobPolling({
    jobId: currentJobId,
  });

  const handleSubmit = async (data: JobSubmission) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Step 1: Create job
      const response = await createJob(
        data.candidateName,
        data.objective,
        data.file.name,
        getFileMimeType(data.file)
      );

      // Step 2: Upload file to pre-signed URL
      await uploadFile(response.uploadUrl, data.file);

      // Step 3: Start polling for status
      setCurrentJobId(response.jobId);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Erro ao enviar currículo. Tente novamente.';
      setSubmitError(errorMessage);
      console.error('Error submitting job:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownload = async () => {
    if (!jobStatus?.outputDownloadUrl) return;

    setIsDownloading(true);

    try {
      const fileName = `CV_Innvo_${jobStatus.jobId}.pdf`;
      await downloadPDF(jobStatus.outputDownloadUrl, fileName);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Erro ao baixar o PDF. Tente novamente.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleNewConversion = () => {
    setCurrentJobId(null);
    setSubmitError(null);
  };

  return (
    <div className="app">
      <header className="app-header">
        <h1 className="app-title">Innvo CV Converter</h1>
        <p className="app-subtitle">
          Sistema de conversão automática de currículos para o padrão Innvo
        </p>
      </header>

      {!currentJobId ? (
        <>
          <UploadForm onSubmit={handleSubmit} isSubmitting={isSubmitting} />
          {submitError && (
            <div className="error-details" style={{ marginTop: '20px' }}>
              <h4>Erro ao enviar:</h4>
              <p>{submitError}</p>
            </div>
          )}
        </>
      ) : (
        <>
          {jobStatus && (
            <JobStatus
              jobStatus={jobStatus}
              onDownload={handleDownload}
              onNewConversion={handleNewConversion}
              isDownloading={isDownloading}
            />
          )}
          {pollingError && (
            <div className="error-details" style={{ marginTop: '20px' }}>
              <h4>Erro ao consultar status:</h4>
              <p>{pollingError}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default App;
